/**
 * Reaction Picker Component
 * 
 * Quick reaction picker with common emojis.
 */

import React, { useState } from 'react';
import { Popover } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

interface ReactionPickerProps {
  onSelectReaction: (emoji: string) => void;
  onRemoveReaction?: () => void;
  currentReaction?: string;
  children?: React.ReactNode;
}

const QuickReactions = ['👍', '❤️', '😂', '😮', '😢', '😡'];

const ReactionsGrid = styled.div`
  display: flex;
  gap: 8px;
  padding: 4px;
`;

const ReactionOption = styled.button<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid ${(props) => (props.isSelected ? '#1890ff' : '#e0e0e0')};
  border-radius: 8px;
  background-color: ${(props) => (props.isSelected ? '#e6f7ff' : '#fff')};
  cursor: pointer;
  font-size: 20px;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${(props) => (props.isSelected ? '#bae7ff' : '#f5f5f5')};
    border-color: ${(props) => (props.isSelected ? '#1890ff' : '#b0b0b0')};
    transform: scale(1.1);
  }
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #ff4d4f;
  border-radius: 8px;
  background-color: #fff;
  cursor: pointer;
  color: #ff4d4f;
  transition: all 0.2s;
  
  &:hover {
    background-color: #fff1f0;
    border-color: #ff7875;
    transform: scale(1.1);
  }
`;

const ReactionPickerContent: React.FC<{
  onSelectReaction: (emoji: string) => void;
  onRemoveReaction?: () => void;
  currentReaction?: string;
  onClose: () => void;
}> = ({ onSelectReaction, onRemoveReaction, currentReaction, onClose }) => {
  const { t } = useTranslation('chat');
  
  const handleClick = (emoji: string) => {
    onSelectReaction(emoji);
    onClose(); // Close popover after selection
  };

  const handleRemove = () => {
    if (onRemoveReaction) {
      onRemoveReaction();
      onClose();
    }
  };

  return (
    <ReactionsGrid>
      {QuickReactions.map((emoji) => (
        <ReactionOption
          key={emoji}
          isSelected={currentReaction === emoji}
          onClick={() => handleClick(emoji)}
          title={emoji}
        >
          {emoji}
        </ReactionOption>
      ))}
      {currentReaction && onRemoveReaction && (
        <RemoveButton
          onClick={handleRemove}
          title={t('reactions.removeReaction')}
        >
          <DeleteOutlined style={{ fontSize: '16px' }} />
        </RemoveButton>
      )}
    </ReactionsGrid>
  );
};

export const ReactionPicker: React.FC<ReactionPickerProps> = ({
  onSelectReaction,
  onRemoveReaction,
  currentReaction,
  children,
}) => {
  const [open, setOpen] = useState(false);
  
  const handleSelect = (emoji: string) => {
    onSelectReaction(emoji);
    setOpen(false);
  };

  const handleRemove = () => {
    if (onRemoveReaction) {
      onRemoveReaction();
      setOpen(false);
    }
  };

  // If children are provided (reactions exist), wrap them in Popover
  if (children) {
    return (
      <Popover
        content={
          <ReactionPickerContent
            onSelectReaction={handleSelect}
            onRemoveReaction={handleRemove}
            currentReaction={currentReaction}
            onClose={() => setOpen(false)}
          />
        }
        trigger="click"
        placement="top"
        overlayStyle={{ padding: 0 }}
        open={open}
        onOpenChange={setOpen}
      >
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
          {children}
        </div>
      </Popover>
    );
  }

  // Otherwise show heart icon (no reactions yet)
  return (
    <Popover
      content={
        <ReactionPickerContent
          onSelectReaction={handleSelect}
          onRemoveReaction={handleRemove}
          currentReaction={currentReaction}
          onClose={() => setOpen(false)}
        />
      }
      trigger="click"
      placement="top"
      overlayStyle={{ padding: 0 }}
      open={open}
      onOpenChange={setOpen}
    >
      <ReactionTrigger hasReaction={false}>
        🤍
      </ReactionTrigger>
    </Popover>
  );
};

const ReactionTrigger = styled.button<{ hasReaction: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: ${(props) => (props.hasReaction ? 'none' : '1px solid #d9d9d9')};
  border-radius: 12px;
  background-color: ${(props) => (props.hasReaction ? 'transparent' : 'transparent')};
  cursor: pointer;
  font-size: ${(props) => (props.hasReaction ? '16px' : '14px')};
  color: ${(props) => (props.hasReaction ? '#f5222d' : '#999')};
  transition: all 0.2s;
  padding: 0;
  
  &:hover {
    color: #f5222d;
    border-color: #f5222d;
    transform: scale(1.1);
  }
`;

